import { t as tr, type TKey } from "@/lib/i18n";
import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { ChevronDown, Pencil, Plus, Sprout } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";

import {
  Badge,
  Button,
  ConfirmModal,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Input,
  Modal,
  PageState,
  Switch,
} from "@fox1ck-org/ui";
import { SettingsActions } from "@/pages/settings/section";
import { ListView } from "@/components/list-view";
import { useTableParams } from "@/lib/hooks/use-table-params";
import { Controller, useForm, type Control } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@fox1ck-org/ui/form";
import { RhfField } from "@/components/rhf-field";
import { goalFormSchema, type GoalFormValues } from "@/lib/schemas/goal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@fox1ck-org/ui";

import { ConversionGoal } from "@gen/tracker/v1/goal_pb";
import { RoleGate } from "@/components/auth/role-gate";
import { useCanWrite } from "@/lib/permissions";
import {
  GOAL_CATEGORIES,
  useCreateGoal,
  useDeleteGoal,
  useListGoals,
  useListPresets,
  useSeedPresetGoals,
  useUpdateGoal,
} from "@/lib/services/goal-service";

// GoalsSettingsPage manages the per-workspace conversion-goal registry: the
// keys stamped on every conversion event (goal.<key>.* metrics derive from
// them). Presets only seed defaults — everything stays editable after.
export default function GoalsSettingsPage() {
  const params = useTableParams({ defaultSort: [{ id: "sortOrder", desc: false }] });
  const includeArchived = params.getFilter("archived") === "1";
  const { data: goals, isLoading, error } = useListGoals(includeArchived);
  const canWrite = useCanWrite();

  const seed = useSeedPresetGoals();
  const presets = useListPresets();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ConversionGoal | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (g: ConversionGoal) => {
    setEditing(g);
    setDialogOpen(true);
  };

  const columns = useMemo<ColumnDef<ConversionGoal>[]>(
    () => [
      {
        id: "key",
        header: tr("param.key"),
        accessorKey: "key",
        cell: ({ row }) => (
          <>
            <span className="rounded-sm bg-surface-2 px-1.5 py-0.5 font-mono text-xs">
              {row.original.key}
            </span>
            {row.original.archived ? (
              <span className="ml-2 text-xs text-muted-foreground">{tr("goals.inArchive")}</span>
            ) : null}
          </>
        ),
      },
      {
        id: "displayName",
        header: tr("flows.col.name"),
        accessorKey: "displayName",
        cell: ({ row }) => row.original.displayName || "—",
      },
      {
        id: "category",
        header: tr("tpl.category"),
        accessorKey: "category",
        cell: ({ row }) => (
          <span className="text-muted-foreground">{goalCategoryLabel(row.original.category)}</span>
        ),
      },
      {
        id: "countsAsRevenue",
        header: tr("goals.col.revenue"),
        accessorKey: "countsAsRevenue",
        meta: { align: "center" },
        cell: ({ row }) => (row.original.countsAsRevenue ? tr("ops.yes") : "—"),
      },
      {
        id: "isPrimary",
        header: tr("goals.col.primary"),
        accessorKey: "isPrimary",
        meta: { align: "center" },
        cell: ({ row }) => (row.original.isPrimary ? <Badge variant="primary">{tr("ops.goals.primary")}</Badge> : "—"),
      },
      {
        id: "allowMultiple",
        header: tr("goals.col.multiple"),
        accessorKey: "allowMultiple",
        meta: { align: "center" },
        cell: ({ row }) => (row.original.allowMultiple ? tr("ops.yes") : "—"),
      },
      {
        id: "sortOrder",
        header: tr("insp.sort"),
        accessorKey: "sortOrder",
        meta: { align: "right" },
        cell: ({ row }) => <span className="tabular-nums">{row.original.sortOrder}</span>,
      },
      ...(canWrite
        ? [
            {
              id: "actions",
              header: "",
              enableSorting: false,
              enableHiding: false,
              meta: { align: "right" as const },
              cell: ({ row }: { row: { original: ConversionGoal } }) => (
                <div className="flex items-center justify-end">
                  <Button variant="ghost" size="icon-sm" onClick={() => openEdit(row.original)} aria-label={tr("flows.edit")} title={tr("flows.edit")}>
                    <Pencil className="size-3.5" strokeWidth={1.75} />
                  </Button>
                </div>
              ),
            } as ColumnDef<ConversionGoal>,
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canWrite],
  );

  return (
    <>
      {canWrite ? (
        <SettingsActions>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" disabled={seed.isPending}>
                <Sprout className="size-4" strokeWidth={1.75} />
                {tr("goals.seed")}
                <ChevronDown className="size-4" strokeWidth={1.75} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{tr("goals.presets")}</DropdownMenuLabel>
              {(presets.data ?? []).map((p) => (
                <DropdownMenuItem
                  key={p.key}
                  className="gap-3"
                  onSelect={() =>
                    seed.mutate(p.key, {
                      onSuccess: (items) => toast.success(tr("goals.seeded", { n: items.length, preset: p.displayName || p.key })),
                      onError: (e) => toast.error(e instanceof Error ? e.message : tr("goals.seedFailed")),
                    })
                  }
                >
                  <span className="flex-1">{p.displayName || p.key}</span>
                  <span className="text-xs text-muted-foreground">{tr("goals.count", { n: p.goals.length })}</span>
                </DropdownMenuItem>
              ))}
              {(presets.data ?? []).length === 0 ? <DropdownMenuItem disabled>{tr("goals.noPresets")}</DropdownMenuItem> : null}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="primary" onClick={openCreate}>
            <Plus className="size-4" strokeWidth={1.75} /> {tr("goals.new")}
          </Button>
        </SettingsActions>
      ) : (
        <SettingsActions>
          <RoleGate need="write" label={tr("goals.new")} />
        </SettingsActions>
      )}
      <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 pb-4 pt-3">
        <div className="flex shrink-0 items-start gap-4">
          <p className="max-w-160 text-sm text-muted-foreground">{tr("ops.goals.about")}</p>
          <label className="ml-auto flex items-center gap-2 text-sm text-muted-foreground">
            <Switch checked={includeArchived} onCheckedChange={(v) => params.setFilter("archived", v ? "1" : null)} />
            {tr("goals.showArchived")}
          </label>
        </div>
        {error ? (
          <PageState mode="error" title={tr("goals.loadError")} error={error} />
        ) : (
          <ListView
            columns={columns}
            data={goals ?? []}
            getRowId={(row) => row.id}
            loading={isLoading}
            density="compact"
            clientPagination
            sorting={params.sorting}
            onSortingChange={params.onSortingChange}
            onRowClick={canWrite ? openEdit : undefined}
            rowClassName={(g) => (g.archived ? "opacity-50" : g.id === editing?.id && dialogOpen ? "bg-surface-2" : undefined)}
            emptyMessage={tr("goals.empty")}
          />
        )}
      </div>

      {/* Keyed on open+target so each opening re-seeds local field state. */}
      <GoalDialog
        key={`${editing?.id ?? "new"}:${dialogOpen}`}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        goal={editing}
      />
    </>
  );
}

function GoalDialog({
  open,
  onOpenChange,
  goal,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goal: ConversionGoal | null;
}) {
  const isEdit = !!goal;
  const create = useCreateGoal();
  const update = useUpdateGoal();
  const remove = useDeleteGoal();

  const form = useForm<GoalFormValues>({
    resolver: zodResolver(goalFormSchema) as never,
    defaultValues: {
      key: goal?.key ?? "",
      displayName: goal?.displayName ?? "",
      category: goal?.category || "custom",
      countsAsRevenue: goal?.countsAsRevenue ?? false,
      isPrimary: goal?.isPrimary ?? false,
      allowMultiple: goal?.allowMultiple ?? false,
      sortOrder: goal?.sortOrder ?? 0,
    },
  });

  const saving = create.isPending || update.isPending;

  const onSubmit = form.handleSubmit((v) => {
    const payload = new ConversionGoal({
      ...(goal ?? {}),
      id: goal?.id ?? "",
      key: goal?.key ?? v.key.trim(), // key is immutable once created
      displayName: v.displayName.trim(),
      category: v.category,
      countsAsRevenue: v.countsAsRevenue,
      isPrimary: v.isPrimary,
      allowMultiple: v.allowMultiple,
      sortOrder: v.sortOrder,
    });
    const mutation = isEdit ? update : create;
    mutation.mutate(payload, {
      onSuccess: () => {
        toast.success(isEdit ? tr("goals.updated") : tr("goals.created"));
        onOpenChange(false);
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : tr("ws.saveFailed")),
    });
  });

  const c = form.control;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const toggleArchived = () => {
    if (!goal) return;
    update.mutate(new ConversionGoal({ ...goal, archived: !goal.archived }), {
      onSuccess: () => {
        toast.success(tr(goal.archived ? "goals.unarchived" : "goals.archived", { key: goal.key }));
        onOpenChange(false);
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : tr("rules.updateFailed")),
    });
  };

  return (
    <>
      <Modal
        open={open}
        onClose={() => onOpenChange(false)}
        size="md"
        title={isEdit ? tr("ops.goals.editTitle", { name: goal!.displayName || goal!.key }) : tr("goals.newTitle")}
        footer={
          <div className="flex w-full items-center gap-2">
            {isEdit ? (
              <>
                <Button variant="ghost" size="sm" onClick={toggleArchived} disabled={update.isPending}>
                  {goal!.archived ? tr("goals.unarchive") : tr("flows.archive")}
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setConfirmDelete(true)}>
                  {tr("rule.ed.delete")}
                </Button>
              </>
            ) : null}
            <Button variant="secondary" className="ml-auto" onClick={() => onOpenChange(false)}>
              {tr("rule.ed.cancel")}
            </Button>
            <Button variant="primary" onClick={onSubmit} disabled={saving}>
              {isEdit ? tr("ws.save") : tr("goals.create")}
            </Button>
          </div>
        }
      >
        <Form {...form}>
          <form className="flex flex-col gap-4" onSubmit={onSubmit}>
            <div className="grid grid-cols-2 gap-3">
              <RhfField control={c} name="key" label={tr("ops.goals.key")} description={isEdit ? tr("goals.key.immutable") : tr("goals.key.new")}>
                {(field) => <Input {...field} placeholder="dep" className="font-mono" disabled={isEdit} />}
              </RhfField>
              <RhfField control={c} name="displayName" label={tr("flows.col.name")}>
                {(field) => <Input {...field} font="sans" placeholder={tr("goals.name.ph")} />}
              </RhfField>
            </div>
            <div className="grid grid-cols-[minmax(0,1fr)_--spacing(30)] gap-3">
              <RhfField control={c} name="category" label={tr("tpl.category")}>
                {(field) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GOAL_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {goalCategoryLabel(cat)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </RhfField>
              <RhfField control={c} name="sortOrder" label={tr("insp.sort")}>
                {(field) => <Input type="number" name={field.name} value={field.value} onBlur={field.onBlur} onChange={(e) => field.onChange(Number(e.target.value) || 0)} />}
              </RhfField>
            </div>
            <div className="flex flex-col gap-3 border-t border-border pt-3">
              <SwitchRow control={c} name="countsAsRevenue" label={tr("goals.revenue")} hint={tr("goals.revenue.hint")} />
              <SwitchRow control={c} name="isPrimary" label={tr("goals.primary")} hint={tr("goals.primary.hint")} />
              <SwitchRow control={c} name="allowMultiple" label={tr("goals.multiple")} hint={tr("goals.multiple.hint")} />
            </div>
          </form>
        </Form>
      </Modal>
      <ConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        tone="danger"
        loading={remove.isPending}
        title={tr("goals.confirmDelete", { key: goal?.key ?? "" })}
        confirmLabel={tr("rule.ed.delete")}
        onConfirm={() => {
          if (!goal) return;
          remove.mutate(goal.id, {
            onSuccess: () => {
              toast.success(tr("goals.deleted", { key: goal.key }));
              setConfirmDelete(false);
              onOpenChange(false);
            },
            onError: (e) => toast.error(e instanceof Error ? e.message : tr("rule.ed.deleteFailed")),
          });
        }}
      />
    </>
  );
}

const GOAL_CATEGORY_LABEL: Record<string, TKey> = {
  registration: "goals.cat.registration",
  deposit: "goals.cat.deposit",
  rebill: "goals.cat.rebill",
  sale: "goals.cat.sale",
  lead: "goals.cat.lead",
  upsell: "goals.cat.upsell",
  install: "goals.cat.install",
  trial: "goals.cat.trial",
  custom: "goals.cat.custom",
};

function goalCategoryLabel(category: string): string {
  if (!category) return "—";
  const key = GOAL_CATEGORY_LABEL[category];
  return key ? tr(key) : category;
}

function SwitchRow({
  control,
  name,
  label,
  hint,
}: {
  control: Control<GoalFormValues>;
  name: "countsAsRevenue" | "isPrimary" | "allowMultiple";
  label: string;
  hint: string;
}) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>
            {label}
            <span className="block text-xs text-muted-foreground">{hint}</span>
          </span>
          <Switch checked={field.value} onCheckedChange={field.onChange} />
        </label>
      )}
    />
  );
}
